import React, { useState } from 'react';
import { HierarchicalTableSkeleton } from '../../Components/Skeletons/ChartSkeletons';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  IconButton,
  Card,
  CardContent,
  Box,
  useTheme
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

interface Subgrupo {
  descricao: string;
  icms_total: number;
}

interface Grupo {
  descricao: string;
  icms_total: number;
  subgrupos: Subgrupo[];
}

interface Secao {
  descricao: string;
  icms_total: number;
  grupos: Grupo[];
}

interface Props {
  data: Secao[];
  title?: string;
  loading?: boolean;
}

const IcmsHierarchicalTable: React.FC<Props> = ({ data, title, loading = false }) => {
  const [openSecoes, setOpenSecoes] = useState<Record<string, boolean>>({});
  const [openGrupos, setOpenGrupos] = useState<Record<string, boolean>>({});
  const theme = useTheme();

  const toggleSecao = (secao: string) => {
    setOpenSecoes((prev) => ({ ...prev, [secao]: !prev[secao] }));
  };

  const toggleGrupo = (grupo: string) => {
    setOpenGrupos((prev) => ({ ...prev, [grupo]: !prev[grupo] }));
  };

  return (
    <Card sx={{ p: 2, height: 590 }}>
      <CardContent sx={{ p: 0 }}>
        {title && !loading && (
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            {title}
          </Typography>
        )}

        {loading ? (
          <HierarchicalTableSkeleton height={550} showTitle={false} />
        ) : (

        <TableContainer
          component={Paper}
          sx={{ maxHeight: 520 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="70%">Categoria</TableCell>
                <TableCell align="right">ICMS Total</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((secao, secaoIndex) => (
                <React.Fragment key={secao.descricao}>
                  <TableRow
                    sx={{
                      backgroundColor: secaoIndex % 2 === 0 ? '#ffffff' : '#f5f5f5'
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => toggleSecao(secao.descricao)}
                          sx={{ mr: 1 }}
                        >
                          {openSecoes[secao.descricao] ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>
                        <Typography fontWeight={600}>
                          {secao.descricao}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {secao.icms_total?.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      })}
                    </TableCell>
                    <TableCell />
                  </TableRow>

                  {openSecoes[secao.descricao] &&
                    secao.grupos.map((grupo, grupoIndex) => (
                      <React.Fragment key={grupo.descricao}>
                        <TableRow
                          sx={{
                            backgroundColor: (secaoIndex * 10 + grupoIndex + 1) % 2 === 0 ? '#ffffff' : '#f5f5f5'
                          }}
                        >
                          <TableCell sx={{ pl: 6 }}>
                            <Box display="flex" alignItems="center">
                              <IconButton
                                size="small"
                                onClick={() => toggleGrupo(grupo.descricao)}
                                sx={{ mr: 1 }}
                              >
                                {openGrupos[grupo.descricao] ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                              <Typography fontWeight={500}>
                                {grupo.descricao}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {grupo.icms_total?.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}
                          </TableCell>
                          <TableCell />
                        </TableRow>

                        {openGrupos[grupo.descricao] &&
                          grupo.subgrupos.map((subgrupo, subgrupoIndex) => (
                            <TableRow 
                              key={subgrupo.descricao}
                              sx={{
                                backgroundColor: (secaoIndex * 100 + grupoIndex * 10 + subgrupoIndex + 2) % 2 === 0 ? '#ffffff' : '#f5f5f5'
                              }}
                            >
                              <TableCell sx={{ pl: 10 }}>
                                {subgrupo.descricao}
                              </TableCell>
                              <TableCell align="right">
                                {subgrupo.icms_total?.toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                })}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          ))}
                      </React.Fragment>
                    ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default IcmsHierarchicalTable;
